import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FinancialProductEnum, productTypeEnum, SegmentOpexType, statusType, TaxType } from './model/open-quote.model';


const EMPTY_HIGHLIGHT_FIELDS = {
  openingFeeWithoutTax: false,
  openingFeeWithTax: false,

  downPaymentWithTax: false,
  downPaymentWithoutTax: false,

  subsidyWithTax: false,
  subsidyWithoutTax: false,

  balloonWithTax: false,
  balloonWithoutTax: false,

  purchaseOptionWithTax: false,
  purchaseWithoutTax: false,

  residualValueWithTax: false,
  residualValueWithoutTax: false,
};
export type OpenQuoteHighlightFields = typeof EMPTY_HIGHLIGHT_FIELDS;

@Injectable({
  providedIn: 'root',
})
export class FinancialCalculatorService {
  currencies = [
    { type: 'MXN', value: 'MXN' },
    { type: 'USD', value: 'USD' },
  ];

  segmentOpex = [
    {
      name: 'Retail Business',
      value: SegmentOpexType.freightLinerRetailBusiness,
    }, //
    {
      name: 'Small Fleets',
      value: SegmentOpexType.freightLinerSmallFleets,
    },
    {
      name: 'Medium fleets',
      value: SegmentOpexType.freightLinerMediumFleets,
    }, //
    {
      name: 'Large fleets',
      value: SegmentOpexType.freightLinerLargeFleets,
    }, //
    {
      name: 'Extra large fleets',
      value: SegmentOpexType.freightLinerExtraFleets,
    }, //
  ];

  tax = [
    { name: 'SI', value: false },
    { name: 'NO', value: true },
  ];

  financialProductsList = [
    { name: 'Crédito directo', value: FinancialProductEnum.directCredit },
    { name: 'Crédito directo', value: FinancialProductEnum.directCreditGlobe },
    {
      name: 'Arrendamiento financiero',
      value: FinancialProductEnum.financialLeasing,
    },
    { name: 'Arrendamiento puro', value: FinancialProductEnum.pureLease },
    { name: 'Capital Loan', value: FinancialProductEnum.capLoan },
    { name: 'Real Estate', value: FinancialProductEnum.realEstate },
  ];

  productType = [
    { name: 'Accesorios', value: productTypeEnum.accesorios, isValidToSelect: () => true },
    { name: 'Autobuses', value: productTypeEnum.autobuses, isValidToSelect: () => true },
    { name: 'Camiones', value: productTypeEnum.camiones, isValidToSelect: () => true },
    { name: 'Tractos', value: productTypeEnum.tractos, isValidToSelect: () => true },
    { name: 'Vans', value: productTypeEnum.vanes, isValidToSelect: (isActive: boolean) => isActive },
    //{ name: 'Non Daimler', value: productTypeEnum.nonDamiler },
  ];

  paymentType = [
    { name: 'Anticipado', value: 'PREPAID' },
    { name: 'Vencimiento', value: 'EXPIRATION ' },
  ];

  specialPaymentType = [
    { name: 'Capital e intereses', value: 'CAPITAL_AND_INTEREST' },
    { name: 'Solo capital', value: 'CAPITAL' },
    { name: 'Solo intereses', value: 'INTEREST' },
  ];

  statusType = [
    // {name: 'Aprobado', value: statusType.approved},
    { name: 'Borrador', value: statusType.draft },
    { name: 'Completo', value: statusType.completed },
    // {name: 'En revisión', value: statusType.InReview},
    { name: 'En progreso', value: statusType.inProgress },
  ];
  readonly emptyHighlightFields: OpenQuoteHighlightFields = {
    openingFeeWithoutTax: false,
    openingFeeWithTax: false,

    downPaymentWithTax: false,
    downPaymentWithoutTax: false,

    subsidyWithTax: false,
    subsidyWithoutTax: false,

    balloonWithTax: false,
    balloonWithoutTax: false,

    purchaseOptionWithTax: false,
    purchaseWithoutTax: false,

    residualValueWithTax: false,
    residualValueWithoutTax: false,
  }

  constructor(private fb: FormBuilder) { }

  smartRound(value: number, decimals: number = 2): number {
    if (!isFinite(value) || isNaN(value) || Number.isInteger(value)) {
      return value;
    }
    // Primero redondear a más decimales para capturar el valor completo
    const extendedDecimals = decimals + 3; // Usar 5 decimales para capturar toda la precisión
    const extendedFactor = Math.pow(10, extendedDecimals);
    const extendedRounded = Math.round(value * extendedFactor) / extendedFactor;

    // Ahora aplicar redondeo paso a paso, decimal por decimal
    let currentValue = extendedRounded;

    // Redondear desde el decimal más lejano hacia el más cercano
    for (let i = extendedDecimals; i > decimals; i--) {
      const factor = Math.pow(10, i - 1);
      currentValue = Math.round(currentValue * factor) / factor;
    }

    return currentValue;
  }

  smartRoundWithCarry(value: number, decimals: number = 2): number {
    if (!isFinite(value) || isNaN(value) || Number.isInteger(value)) {
      return value;
    }

    // Redondear primero a los decimales especificados
    const factor = Math.pow(10, decimals);
    let currentValue = Math.round(value * factor) / factor;

    // si primer decimal es 0 y segundo decimal < 5, redondear a .00
    const checkSpecialRule = (num: number): number => {
      const str = num.toString();
      const decimalIndex = str.indexOf('.');

      if (decimalIndex === -1) return num; // No tiene decimales

      const decimalPart = str.substring(decimalIndex + 1);

      // Verificar si tiene al menos 2 decimales
      if (decimalPart.length >= 2) {
        const firstDecimal = parseInt(decimalPart[0]);
        const secondDecimal = parseInt(decimalPart[1]);

        // Si primer decimal es 0 y segundo decimal < 5, redondear a entero
        if (firstDecimal === 0 && secondDecimal < 5) {
          return Math.floor(num);
        }
      }

      return num;
    };

    currentValue = checkSpecialRule(currentValue);

    const needsMoreRounding = (num: number): boolean => {
      const str = num.toString();
      const decimalIndex = str.indexOf('.');

      // Si no tiene decimales, no necesita más redondeo
      if (decimalIndex === -1) return false;

      const decimalPart = str.substring(decimalIndex + 1);

      // Si no tiene decimales después del punto, no necesita redondeo
      if (decimalPart.length === 0) return false;

      // Verificar si el último dígito es > 5
      const lastDigit = parseInt(decimalPart[decimalPart.length - 1]);
      return lastDigit > 5;
    };

    // Función para redondear eliminando un decimal
    const roundUpOneLevel = (num: number): number => {
      const str = num.toString();
      const decimalIndex = str.indexOf('.');

      if (decimalIndex === -1) return num;

      const decimalPart = str.substring(decimalIndex + 1);
      if (decimalPart.length <= 1) {
        return Math.ceil(num);
      }

      const newDecimals = decimalPart.length - 1;
      const newFactor = Math.pow(10, newDecimals);
      return Math.round(num * newFactor) / newFactor;
    };

    // Aplicar redondeo en cascada hasta que no sea necesario más
    let maxIterations = 10;
    while (needsMoreRounding(currentValue) && maxIterations > 0) {
      currentValue = roundUpOneLevel(currentValue);
      maxIterations--;
    }

    return currentValue;
  }

  smartRoundSimple(value: number, decimals: number = 2): number {
    if (!isFinite(value) || isNaN(value)) {
      return value;
    }

    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  calculateWithoutIVA(totalWithIVA: number, ivaRate: number = 0.16, decimals: number = 0): any {
    const valueWithoutIVA = totalWithIVA / (1 + ivaRate);
    return this.round2(valueWithoutIVA);
  }

  getApiResultHighlight(form: FormGroup) {
    const financialProductType = form.controls['financialProductType'].value;
    const purchaseOptionType = form.controls['purchaseOptionType'].value;
    const residualValueType = form.controls['residualValueType'].value;
    const subsidyType = form.controls['subsidyType'].value;
    const balloonType = form.controls['balloonType']?.value;

    // Crear una copia del objeto vacío para no mutar el original
    const highlightFields = { ...this.emptyHighlightFields };

    // Si no hay producto financiero seleccionado, devolver campos vacíos
    if (!financialProductType) {
      return highlightFields;
    }

    // Crédito directo
    // Comisión por apertura: solo tiene una base de cálculo, devuelve con IVA
    // Subsidio: resaltar valor sin IVA, el subsidio no afecta al cálculo
    if (financialProductType === FinancialProductEnum.directCredit || financialProductType === FinancialProductEnum.directCreditGlobe) {
      highlightFields.openingFeeWithTax = true;
      if (subsidyType) {
        highlightFields.subsidyWithoutTax = true;
      }
      if (balloonType) {
        if (balloonType === TaxType.invoiceWithTax) {
          highlightFields.balloonWithTax = true;
        } else {
          highlightFields.balloonWithoutTax = true;
        }
      }
    }
    // Arrendamiento financiero
    // Comisión por apertura: solo tiene una base de cálculo, devuelve con IVA
    // Subsidio: resaltar valor sin IVA, el subsidio no afecta al cálculo
    // Opción de compra según el tipo de base de cálculo
    else if (financialProductType === FinancialProductEnum.financialLeasing) {
      highlightFields.openingFeeWithTax = true;
      if (subsidyType) {
        highlightFields.subsidyWithoutTax = true;
      }
      if (purchaseOptionType) {
        if (purchaseOptionType === TaxType.invoiceWithTax) {
          // Valor con IVA: devuelve monto sin IVA
          highlightFields.purchaseOptionWithTax = true;
        } else if (purchaseOptionType === TaxType.invoiceWithoutTax) {
          // Valor sin IVA: devuelve monto sin IVA
          highlightFields.purchaseWithoutTax = true;
        } else if (purchaseOptionType === TaxType.amountToFinance) {
          // Monto a financiar: devuelve monto con IVA
          highlightFields.purchaseOptionWithTax = true;
        } else if (purchaseOptionType === TaxType.fixedAmount) {
          // Monto fijo: devuelve monto sin IVA
          highlightFields.purchaseWithoutTax = true;
        }
      }

      if (balloonType) {
        if (balloonType === TaxType.invoiceWithTax) {
          highlightFields.balloonWithTax = true;
        } else {
          highlightFields.balloonWithoutTax = true;
        }
      }
    }
    // Arrendamiento puro
    // Subsidio: resaltar valor sin IVA, el subsidio no afecta al cálculo
    // Valor residual: todos los casos devuelven sin IVA
    else if (financialProductType === FinancialProductEnum.pureLease) {
      highlightFields.openingFeeWithTax = true;
      if (subsidyType) {
        highlightFields.subsidyWithoutTax = true;
      }
      if (residualValueType) {
        if (residualValueType === TaxType.amountToFinance) {
          // Monto a financiar: devuelve sin IVA
          highlightFields.residualValueWithoutTax = true;
        } else if (residualValueType === TaxType.invoiceWithTax) {
          // Valor de la unidad con IVA: devuelve sin IVA
          highlightFields.residualValueWithTax = true;
        } else if (residualValueType === TaxType.invoiceWithoutTax) {
          // Valor de la unidad sin IVA: devuelve sin IVA
          highlightFields.residualValueWithoutTax = true;
        } else if (residualValueType === TaxType.fixedAmount) {
          // Monto fijo: devuelve sin IVA
          highlightFields.residualValueWithoutTax = true;
        }
      }
    }
    // Crédito directo Balloon (similar a crédito directo)
    // Comisión por apertura: solo tiene una base de cálculo, devuelve con IVA
    // Subsidio: resaltar valor sin IVA, el subsidio no afecta al cálculo
    // Balloon: resaltar según su configuración
    // else if (financialProductType === FinancialProductEnum.directCreditGlobe) {
    //   highlightFields.openingFeeWithTax = true;
    //   if (subsidyType) {
    //     highlightFields.subsidyWithoutTax = true;
    //   }
    //   const balloonType = form.controls['balloonType']?.value;
    //   if (balloonType) {
    //     // Para balloon, generalmente se resalta sin IVA
    //     highlightFields.balloonWithoutTax = true;
    //   }
    // }

    return highlightFields;
  }

  calculateAdditionalCollateral(form: FormGroup) {
    const additionalCollateralPercentage =
      form.controls['additionalCollateralPercentage'].value;
    const additionalCollateralType =
      form.controls['additionalCollateralType'].value;
    const rate = form.controls['taxFree'].value;
    let additionalCollateralTotal = 0;

    if (!additionalCollateralType) return;

    if (rate) {
      if (
        !form.controls['unitPriceWithoutTax'].value ||
        !additionalCollateralPercentage
      )
        return;
      if (additionalCollateralType == 'UNIT_VALUE') {
        const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
        additionalCollateralTotal =
          unitPriceWithoutTax * (additionalCollateralPercentage / 100);
      } else {
        const totalPriceWithoutTax =
          form.controls['totalPriceWithoutTax'].value;
        additionalCollateralTotal =
          totalPriceWithoutTax * (additionalCollateralPercentage / 100);
      }
    } else {
      if (
        !form.controls['unitPriceWithTax'].value ||
        !additionalCollateralPercentage
      )
        return;
      if (additionalCollateralType == 'UNIT_VALUE') {
        const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
        additionalCollateralTotal =
          unitPriceWithTax * (additionalCollateralPercentage / 100);
      } else {
        const totalPriceWithTax = form.controls['totalPriceWithTax'].value;
        additionalCollateralTotal =
          totalPriceWithTax * (additionalCollateralPercentage / 100);
      }
    }

    return this.round2(additionalCollateralTotal);
  }

  /**
   * Pure calculation function for purchase option - can be reused without FormGroup dependency
   * @param purchaseOptionType Type of purchase option calculation
   * @param purchasePercentage Percentage to apply (can be null/undefined)
   * @param purchaseFixedAmount Fixed amount for purchase option (can be null/undefined)
   * @param tax Tax percentage
   * @param unitPriceWithTax Unit price including tax
   * @param unitPriceWithoutTax Unit price without tax
   * @param financialAmount Amount to finance
   * @returns Calculated purchase option value as string with 2 decimal places
   */
  calculatePurchasePure(
    purchaseOptionType: TaxType,
    purchasePercentage: number | null | undefined,
    purchaseFixedAmount: number | null | undefined,
    tax: number,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    financialAmount: number
  ): string {
    let purchaseTotal = 0;
    const isPercentageEmpty = purchasePercentage == null || purchasePercentage == undefined;

    if (purchaseOptionType == TaxType.invoiceWithTax) {
      if (!unitPriceWithTax || isPercentageEmpty)
        return '0.00';
      purchaseTotal = (unitPriceWithTax * (purchasePercentage / 100)) * (1 + tax / 100);
      // purchaseTotal =  (unitPriceWithTax * (purchasePercentage / 100));
    } else if (purchaseOptionType == TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isPercentageEmpty)
        return '0.00';
      // purchaseTotal = ( unitPriceWithoutTax * (purchasePercentage / 100)) * (1 + tax / 100);
      purchaseTotal = (unitPriceWithTax * (purchasePercentage / 100));
    } else if (purchaseOptionType == TaxType.amountToFinance) {
      if (!financialAmount || isPercentageEmpty)
        return '0.00';
      purchaseTotal = financialAmount * (purchasePercentage / 100);
    } else if (purchaseOptionType == TaxType.fixedAmount) {
      if (purchaseFixedAmount == null || purchaseFixedAmount == undefined)
        return '0.00';
      purchaseTotal = parseFloat(purchaseFixedAmount.toString());
    }

    return String(this.round2(purchaseTotal));
  }

  calculatePurchase(form: FormGroup) {
    const purchaseOptionType = form.controls['purchaseOptionType']?.value;
    const purchasePercentage = form.controls['purchasePercentage']?.value;
    const purchaseFixedAmount = form.controls['purchaseTotal']?.value;
    const tax = form.controls['tax']?.value;
    const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    const financialAmount = form.controls['financialAmount'].value;

    return this.calculatePurchasePure(
      purchaseOptionType,
      purchasePercentage,
      purchaseFixedAmount,
      tax,
      unitPriceWithTax,
      unitPriceWithoutTax,
      financialAmount
    );
  }

  getPurchaseFields(type: TaxType) {
    if (type == TaxType.invoiceWithTax) {
      return { withTax: "purchaseTotal", withoutTax: "purchaseTotalWithoutTax" };
    } else if (type == TaxType.invoiceWithoutTax) {
      return { withTax: "purchaseTotalWithoutTax", withoutTax: "purchaseTotal" };
    } else if (type == TaxType.amountToFinance) {
      return { withTax: "purchaseTotal", withoutTax: "purchaseTotalWithoutTax" };
    } else {
      return { withTax: "purchaseTotal", withoutTax: "purchaseTotalWithoutTax" };
    }
  }

  /**
   * Pure calculation function for residual value - can be reused without FormGroup dependency
   * @param residualValueType Type of residual value calculation
   * @param residualValuePercentage Percentage to apply (can be null/undefined)
   * @param residualValueFixedAmount Fixed amount for residual value (can be null/undefined)
   * @param unitPriceWithTax Unit price including tax
   * @param unitPriceWithoutTax Unit price without tax
   * @param financialAmount Amount to finance
   * @returns Calculated residual value as string with 2 decimal places
   */
  calculateResidualValuePure(
    residualValueType: TaxType,
    residualValuePercentage: number | null | undefined,
    residualValueFixedAmount: number | null | undefined,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    financialAmount: number
  ): string {
    const isResidualValuePercentageEmpty = residualValuePercentage == null || residualValuePercentage == undefined;
    let residualValueTotal = 0;

    if (residualValueType == TaxType.invoiceWithTax) {
      if (!unitPriceWithTax || isResidualValuePercentageEmpty)
        return '0.00';
      residualValueTotal = unitPriceWithTax * (residualValuePercentage / 100);
    } else if (residualValueType == TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isResidualValuePercentageEmpty)
        return '0.00';
      residualValueTotal = unitPriceWithoutTax * (residualValuePercentage / 100);
    } else if (residualValueType == TaxType.amountToFinance) {
      if (!financialAmount || isResidualValuePercentageEmpty)
        return '0.00';
      residualValueTotal = financialAmount * (residualValuePercentage / 100);
    } else {
      if (residualValueFixedAmount == null || residualValueFixedAmount == undefined)
        return '0.00';
      residualValueTotal = Number(residualValueFixedAmount);
      if (!Number.isFinite(residualValueTotal)) {
        return '0.00';
      }
    }

    return String(this.round2(residualValueTotal));
  }

  calculateResidualValue(form: FormGroup) {
    const residualValueType = form.controls['residualValueType'].value;
    const residualValuePercentage =
      form.controls['residualValuePercentage'].value;
    const residualValueFixedAmount = form.controls['residualValueTotal'].value;
    const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    const financialAmount = form.controls['financialAmount'].value;

    return this.calculateResidualValuePure(
      residualValueType,
      residualValuePercentage,
      residualValueFixedAmount,
      unitPriceWithTax,
      unitPriceWithoutTax,
      financialAmount
    );
  }

  getResidualFields(type: TaxType) {
    if (type == TaxType.invoiceWithTax) {
      return { withTax: "residualValueTotalWithTax", withoutTax: "residualValueTotal" };
    } else if (type == TaxType.invoiceWithoutTax) {
      return { withTax: "residualValueTotalWithTax", withoutTax: "residualValueTotal" };
    } else if (type == TaxType.amountToFinance) {
      return { withTax: "residualValueTotalWithTax", withoutTax: "residualValueTotal" };
    } else {
      return { withTax: "residualValueTotalWithTax", withoutTax: "residualValueTotal" };
    }
  }

  /**
   * Función pura para calcular el subsidio basado en parámetros
   * @param subsidyType - Tipo de subsidio
   * @param subsidyPercentage - Porcentaje del subsidio
   * @param commisionFixedAmount - Monto fijo del subsidio
   * @param tax - Porcentaje de impuesto
   * @param unitPriceWithTax - Precio unitario con impuestos
   * @param unitPriceWithoutTax - Precio unitario sin impuestos
   * @param financialAmount - Monto a financiar
   * @returns El monto del subsidio calculado
   */
  calculateSubsidyAmount(
    subsidyType: TaxType,
    subsidyPercentage: number,
    commisionFixedAmount: number,
    tax: number,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    financialAmount: number
  ): string {
    let subsidyTotal = 0;
    const isSubsidyPercentageEmpty = subsidyPercentage == null || subsidyPercentage == undefined;

    if (subsidyType == TaxType.invoiceWithTax) {
      if (!unitPriceWithTax || isSubsidyPercentageEmpty)
        return '0';
      subsidyTotal = unitPriceWithTax * (subsidyPercentage / 100) * (1 + tax / 100);
    } else if (subsidyType == TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isSubsidyPercentageEmpty)
        return '0';
      subsidyTotal = unitPriceWithoutTax * (subsidyPercentage / 100);
    } else if (subsidyType == TaxType.amountToFinance) {
      if (!financialAmount || isSubsidyPercentageEmpty) return '0';
      subsidyTotal = financialAmount * (subsidyPercentage / 100);
    } else {
      if (!commisionFixedAmount) return '0';
      subsidyTotal = parseFloat(commisionFixedAmount.toString());
    }

    return String(this.round2(subsidyTotal));
  }


  calculateSubsidy(form: FormGroup) {
    const subsidyType = form.controls['subsidyType'].value;
    const subsidyPercentage = form.controls['subsidyPercentage'].value;
    const commisionFixedAmount = form.controls['subsidyTotal'].value;
    let tax = form.controls['tax']?.value;
    const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    const financialAmount = form.controls['financialAmount'].value;

    return this.calculateSubsidyAmount(
      subsidyType,
      subsidyPercentage,
      commisionFixedAmount,
      tax,
      unitPriceWithTax,
      unitPriceWithoutTax,
      financialAmount
    );
  }

  calculateBalloonAmount(form: FormGroup) {
    const balloonType = form.controls['balloonType'].value;
    const balloonPercentage = form.controls['balloonPercentage'].value;
    const commisionFixedAmount = form.controls['balloon'].value;
    const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    const financialAmount = form.controls['financialAmount'].value;

    return this.calculateBalloonPure(balloonType, balloonPercentage, commisionFixedAmount, unitPriceWithTax, unitPriceWithoutTax, financialAmount);
  }

  getSubsidyFields(type: TaxType) {
    if (type == TaxType.invoiceWithTax) {
      return { withTax: "subsidyTotal", withoutTax: "subsidyTotalWithoutTax" };
    } else if (type == TaxType.invoiceWithoutTax) {
      return { withTax: "subsidyTotalWithTax", withoutTax: "subsidyTotal" };
    } else if (type == TaxType.amountToFinance) {
      return { withTax: "subsidyTotalWithTax", withoutTax: "subsidyTotal" };
    } else {
      return { withTax: "subsidyTotalWithTax", withoutTax: "subsidyTotal" };
    }
  }

  getBalloonFields(type: TaxType) {
    if (type == TaxType.invoiceWithTax) {
      return { withTax: "balloon", withoutTax: "balloonWithoutTax" };
    } else if (type == TaxType.invoiceWithoutTax) {
      return { withTax: "balloonWithTax", withoutTax: "balloon" };
    } else {
      return { withTax: "balloonWithTax", withoutTax: "balloon" };
    }
  }

  calculateAdditional(calcType: TaxType, valueCalculated: any, tax: any) {
    const taxDecimal = Number(parseFloat(tax) / 100);
    let result = 0;
    if (calcType == TaxType.invoiceWithTax) {
      const valueWithTax = Number(valueCalculated) || 0;
      const totalWithoutTax = valueWithTax * (1 + taxDecimal);
      // Usar redondeo matemático correcto para valor sin IVA
      result = totalWithoutTax;
    } else if (calcType == TaxType.invoiceWithoutTax || calcType == TaxType.amountToFinance || calcType == TaxType.fixedAmount) {
      const valueWithoutTax = valueCalculated;
      const rawPurchaseTotalWithTax = Number(valueWithoutTax) * (1 + taxDecimal);
      const roundedWithTax = isNaN(rawPurchaseTotalWithTax) ? 0 : rawPurchaseTotalWithTax;
      result = roundedWithTax;
    }
    return String(this.round2(result));
  }

  calculateBalloon(form: FormGroup) {
    const balloonType = form.controls['balloonType'].value;
    const balloonPercentage = form.controls['balloonPercentage'].value;
    const commisionFixedAmount = form.controls['balloon'].value;
    let balloon = 0;

    if (balloonType == TaxType.invoiceWithTax) {
      if (!form.controls['unitPriceWithTax'].value || !balloonPercentage)
        return;
      const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
      balloon = unitPriceWithTax * (balloonPercentage / 100);
    } else if (balloonType == TaxType.invoiceWithoutTax) {
      if (!form.controls['unitPriceWithoutTax'].value || !balloonPercentage)
        return;
      const priceWithoutTax = form.controls['unitPriceWithoutTax'].value;
      balloon = priceWithoutTax * (balloonPercentage / 100);
    } else if (balloonType == TaxType.amountToFinance) {
      if (!form.controls['financialAmount'].value || !balloonPercentage) return;
      const financialAmount = form.controls['financialAmount'].value;
      balloon = financialAmount * (balloonPercentage / 100);
    } else {
      if (!commisionFixedAmount) return;
      balloon = parseFloat(commisionFixedAmount);
    }

    return String(this.round2(balloon));
  }

  /**
   * Función pura para calcular la comisión por apertura basado en parámetros
   * @param commissionForOpeningTaxType - Tipo de impuesto de la comisión por apertura
   * @param commissionForOpeningTax - Porcentaje de la comisión por apertura
   * @param commisionFixedAmount - Monto fijo de la comisión por apertura
   * @param downPayment - Monto del enganche
   * @param financialProduct - Tipo de producto financiero
   * @param financialAmount - Monto financiero
   * @param priceWithTax - Precio con impuestos
   * @param tax - Porcentaje de impuesto
   * @param priceWithoutTax - Precio sin impuestos
   * @returns El monto de la comisión por apertura calculado
   */
  calculateCommissionForOpeningAmount(
    commissionForOpeningTaxType: TaxType,
    commissionForOpeningTax: number,
    commisionFixedAmount: any,
    downPayment: number,
    financialProduct: FinancialProductEnum,
    financialAmount: number,
    priceWithTax: number,
    tax: number,
    priceWithoutTax: number
  ): number {
    let commissionForOpening = 0;
    const taxDecimal = Number(tax) / 100
    const isNotValidCommissionForOpening = commissionForOpeningTax == null || commissionForOpeningTax == undefined;
    const isNotValidDownPayment = downPayment == null || downPayment == undefined;

    if (!commissionForOpeningTaxType || !financialProduct) return 0;
    if ((financialProduct == FinancialProductEnum.directCredit || financialProduct == FinancialProductEnum.financialLeasing
      || financialProduct == FinancialProductEnum.directCreditGlobe || financialProduct == FinancialProductEnum.capLoan
      || financialProduct == FinancialProductEnum.realEstate
    ) && isNotValidDownPayment) {
      return 0;
    }
    //Crédito directo
    if (financialProduct == FinancialProductEnum.directCredit || financialProduct == FinancialProductEnum.directCreditGlobe
    ) {
      const calcWithoutIVA = (priceWithTax - downPayment) * (commissionForOpeningTax / 100);
      if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA;
      } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA * (1 + taxDecimal);
      } else {
        if (commisionFixedAmount == null) return 0;
        commissionForOpening = parseFloat(commisionFixedAmount);
      }
      //Arrendamiento puro
    } else if (financialProduct == FinancialProductEnum.pureLease) {
      const commissionForOpeningWithoutIVA = (priceWithoutTax) * (commissionForOpeningTax / 100);
      if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
        if (!financialAmount || isNotValidCommissionForOpening)
          return 0;
        commissionForOpening = commissionForOpeningWithoutIVA;
      } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
        if (!financialAmount || isNotValidCommissionForOpening)
          return 0;
        commissionForOpening = commissionForOpeningWithoutIVA * (1 + taxDecimal);
      } else {
        if (commisionFixedAmount == null) return 0;
        commissionForOpening = parseFloat(commisionFixedAmount);
      }
      //Arrendamiento financiero
    } else if (financialProduct == FinancialProductEnum.financialLeasing) {
      const calculatedFinancialAmount = (priceWithTax - downPayment) / (1 + taxDecimal);
      const calcWithoutIVA = calculatedFinancialAmount * (commissionForOpeningTax / 100);
      if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA;
      } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA * (1 + taxDecimal);
      } else {
        if (commisionFixedAmount == null) {
          return 0;
        }
        commissionForOpening = parseFloat(commisionFixedAmount);
      }
    }
    // else if (financialProduct == FinancialProductEnum.directCreditGlobe) {
    //   const calcWithoutIVA = (priceWithTax - downPayment) * (commissionForOpeningTax / 100);
    //   if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
    //     if (!financialAmount || isNotValidCommissionForOpening) {
    //       return 0;
    //     }
    //     commissionForOpening = calcWithoutIVA;
    //   } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
    //     if (!financialAmount || isNotValidCommissionForOpening) {
    //       return 0;
    //     }
    //     commissionForOpening = calcWithoutIVA * (1 + taxDecimal);
    //   } else {
    //     if (commisionFixedAmount == null) return 0;
    //     commissionForOpening = parseFloat(commisionFixedAmount);
    //   }
    // }
    else if (financialProduct == FinancialProductEnum.capLoan) {
      const calcWithoutIVA = (priceWithTax - downPayment) * (commissionForOpeningTax / 100);
      if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA;
      } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA * (1 + taxDecimal);
      } else {
        if (commisionFixedAmount == null) return 0;
        commissionForOpening = parseFloat(commisionFixedAmount);
      }
    } else if (financialProduct == FinancialProductEnum.realEstate) {
      const calcWithoutIVA = (priceWithTax - downPayment) * (commissionForOpeningTax / 100);
      if (commissionForOpeningTaxType == TaxType.invoiceWithoutTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA;
      } else if (commissionForOpeningTaxType == TaxType.invoiceWithTax) {
        if (!financialAmount || isNotValidCommissionForOpening) {
          return 0;
        }
        commissionForOpening = calcWithoutIVA * (1 + taxDecimal);
      } else {
        if (commisionFixedAmount == null) return 0;
        commissionForOpening = parseFloat(commisionFixedAmount);
      }
    }

    return commissionForOpening > 0 ? this.round2(commissionForOpening) : 0;
  }

  calculateCommissionForOpening(form: FormGroup, type: TaxType) {
    const commissionForOpeningTaxType = type;
    const commissionForOpeningTax = form.controls['openingFeePercentage'].value;
    const commisionFixedAmount = form.controls['openingFee'].value;
    const downPayment = form.controls['downPayment'].value;
    const financialProduct = form.controls['financialProductType'].value;
    const financialAmount = form.controls['financialAmount'].value;
    const priceWithTax = form.controls['unitPriceWithTax'].value;
    const tax = form.controls['tax'].value;
    const priceWithoutTax = form.controls['unitPriceWithoutTax'].value;

    return this.calculateCommissionForOpeningAmount(
      commissionForOpeningTaxType,
      commissionForOpeningTax,
      commisionFixedAmount,
      downPayment,
      financialProduct,
      financialAmount,
      priceWithTax,
      tax,
      priceWithoutTax
    );
  }


  /**
   * Función pura para calcular el monto financiero basado en parámetros
   * @param financialProduct - Tipo de producto financiero
   * @param downPayment - Monto del enganche
   * @param priceWithoutTax - Precio sin impuestos
   * @param priceWithTax - Precio con impuestos
   * @param tax - Porcentaje de impuesto
   * @returns El monto financiero calculado
   */
  calculateFinancialAmountValue(
    financialProduct: FinancialProductEnum,
    downPayment: number,
    priceWithoutTax: number,
    priceWithTax: number,
    tax: number
  ): string {
    const taxDecimal = Number(tax / 100);
    let financialAmount = 0;

    if (financialProduct == FinancialProductEnum.pureLease) {
      financialAmount = priceWithoutTax;
    } else if (financialProduct == FinancialProductEnum.financialLeasing) {
      financialAmount = (priceWithTax - downPayment) / (1 + taxDecimal);
    } else if (financialProduct == FinancialProductEnum.directCredit || financialProduct == FinancialProductEnum.directCreditGlobe) {
      financialAmount = priceWithTax - downPayment;
    } else if (financialProduct == FinancialProductEnum.capLoan) {
      financialAmount = priceWithTax - downPayment;
    } else if (financialProduct == FinancialProductEnum.realEstate) {
      financialAmount = priceWithTax - downPayment;
    }

    return String(this.round2(financialAmount));
  }

  calculateFinancialAmount(form: FormGroup) {
    const financialProduct = form.controls['financialProductType'].value;
    const downPayment = parseFloat(form.controls['downPayment'].value) || 0;
    const priceWithoutTax = parseFloat(form.controls['unitPriceWithoutTax'].value) || 0;
    const priceWithTax = parseFloat(form.controls['unitPriceWithTax'].value) || 0;
    const tax = form.controls['tax'].value;

    return this.calculateFinancialAmountValue(
      financialProduct,
      downPayment,
      priceWithoutTax,
      priceWithTax,
      tax
    );
  }


  /**
   * Función pura para calcular el enganche basado en parámetros
   * @param downPaymentTaxType - Tipo de impuesto del enganche
   * @param downPaymentTax - Porcentaje del enganche
   * @param unitPriceWithTax - Precio unitario con impuestos
   * @param unitPriceWithoutTax - Precio unitario sin impuestos
   * @param downPaymentFixedAmount - Cantidad fija del enganche
   * @returns El monto del enganche calculado
   */
  calculateDownPaymentAmount(
    downPaymentTaxType: TaxType,
    downPaymentTax: number,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    downPaymentFixedAmount: number
  ): string | number {
    let downPayment = 0;

    if (downPaymentTaxType === undefined || downPaymentTaxType === null) return 0;

    if (downPaymentTaxType === TaxType.invoiceWithTax) {
      if (
        unitPriceWithTax !== undefined &&
        unitPriceWithTax !== null &&
        downPaymentTax !== undefined &&
        downPaymentTax !== null
      ) {
        downPayment = unitPriceWithTax * (downPaymentTax / 100);
      }
    } else if (downPaymentTaxType === TaxType.invoiceWithoutTax) {
      if (unitPriceWithoutTax && downPaymentTax && downPaymentTax != 0 && unitPriceWithoutTax != 0) {
        downPayment = unitPriceWithoutTax * (downPaymentTax / 100);
      }
    } else {
      if (typeof downPaymentFixedAmount === 'number') {
        downPayment = downPaymentFixedAmount;
      }
    }

    return typeof downPayment === 'number' ? this.round2(downPayment) : 0;
  }

  calculateDownPayment(form: FormGroup) {
    const downPaymentTaxType = form.controls['downPaymentTaxType'].value;
    const downPaymentTax = form.controls['downPaymentTax'].value;
    const unitPriceWithTax = form.controls['unitPriceWithTax'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    const downPaymentFixedAmount = form.controls['downPayment'].value;

    return this.calculateDownPaymentAmount(
      downPaymentTaxType,
      downPaymentTax,
      unitPriceWithTax,
      unitPriceWithoutTax,
      downPaymentFixedAmount
    );
  }

  calculateDepositsInGuarantee(form: FormGroup) {
    const warrantyDepositType = form.controls['warrantyDeposit'].value;
    const warrantyDepositPercentage = form.controls['warrantyDepositPercentage'].value;
    const warrantyDepositoTotal = form.controls['warrantyDepositTotal'].value;
    const warrantyDepositMonths = form.controls['warrantyDepositMonth'].value;
    const term = form.controls['term'].value;
    const monthlyPayment = form.controls['monthlyPayment'].value;
    let warrantyDepositAmount = 0;

    if (warrantyDepositType == null || warrantyDepositType == undefined || warrantyDepositType == "" || warrantyDepositType == "null") return 0;
    const isPercentageEmpty = warrantyDepositPercentage == null || warrantyDepositPercentage == undefined;
    const tax = form.controls['tax']?.value;
    const financialAmount = form.controls['financialAmount'].value;
    const unitPriceWithoutTax = form.controls['unitPriceWithoutTax'].value;
    if (warrantyDepositType == TaxType.invoiceWithTax) {
      if (!unitPriceWithoutTax || isPercentageEmpty) return '0.00';
      warrantyDepositAmount = (unitPriceWithoutTax * (warrantyDepositPercentage / 100)) * (1 + tax / 100);
    } else if (warrantyDepositType == TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isPercentageEmpty) return '0.00';
      warrantyDepositAmount = (unitPriceWithoutTax * (warrantyDepositPercentage / 100));
    } else if (warrantyDepositType == TaxType.fixedAmount) {
      if (!warrantyDepositoTotal) return 0;
      warrantyDepositAmount = parseFloat(warrantyDepositoTotal);
    } else if (warrantyDepositType == TaxType.numberOfRents) {
      if (!warrantyDepositMonths) return '0.00';
      if (monthlyPayment !== null && monthlyPayment !== undefined && monthlyPayment !== '') {
        warrantyDepositAmount = Number(monthlyPayment) * warrantyDepositMonths;
      } else {
        warrantyDepositAmount = (financialAmount / term) * warrantyDepositMonths;
      }
    }
    return String(this.round2(warrantyDepositAmount));
  }

  /**
   * Función pura para calcular el balloon basado en parámetros
   * @param balloonType - Tipo de balloon
   * @param balloonPercentage - Porcentaje del balloon
   * @param balloonFixedAmount - Monto fijo del balloon
   * @param unitPriceWithTax - Precio unitario con impuestos
   * @param unitPriceWithoutTax - Precio unitario sin impuestos
   * @param financialAmount - Monto a financiar
   * @returns El monto del balloon calculado
   */
  calculateBalloonPure(
    balloonType: TaxType,
    balloonPercentage: number | null | undefined,
    balloonFixedAmount: number | null | undefined,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    financialAmount: number
  ): string {
    let balloon = 0;
    const isBalloonPercentageEmpty = balloonPercentage == null || balloonPercentage == undefined;

    if (balloonType === TaxType.invoiceWithTax) {
      if (!unitPriceWithTax || isBalloonPercentageEmpty)
        return '0.00';
      balloon = unitPriceWithTax * (balloonPercentage / 100);
    } else if (balloonType === TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isBalloonPercentageEmpty)
        return '0.00';
      balloon = unitPriceWithoutTax * (balloonPercentage / 100);
    } else if (balloonType === TaxType.amountToFinance) {
      if (!financialAmount || isBalloonPercentageEmpty)
        return '0.00';
      balloon = financialAmount * (balloonPercentage / 100);
    } else if (balloonType === TaxType.fixedAmount) {
      if (balloonFixedAmount == null || balloonFixedAmount == undefined)
        return '0.00';
      balloon = parseFloat(balloonFixedAmount.toString());
    }

    return String(this.round2(balloon));
  }

  /**
   * Función pura para calcular el depósito en garantía basado en parámetros
   * @param warrantyDepositType - Tipo de depósito en garantía
   * @param warrantyDepositPercentage - Porcentaje del depósito en garantía
   * @param warrantyDepositFixedAmount - Monto fijo del depósito en garantía
   * @param unitPriceWithTax - Precio unitario con impuestos
   * @param unitPriceWithoutTax - Precio unitario sin impuestos
   * @param financialAmount - Monto a financiar
   * @returns El monto del depósito en garantía calculado
   */
  calculateWarrantyDepositPure(
    warrantyDepositType: TaxType,
    warrantyDepositPercentage: number | null | undefined,
    warrantyDepositFixedAmount: number | null | undefined,
    unitPriceWithTax: number,
    unitPriceWithoutTax: number,
    financialAmount: number
  ): string {
    let warrantyDepositTotal = 0;
    const isWarrantyDepositPercentageEmpty = warrantyDepositPercentage == null || warrantyDepositPercentage == undefined;

    if (warrantyDepositType === TaxType.invoiceWithTax) {
      if (!unitPriceWithTax || isWarrantyDepositPercentageEmpty)
        return '0.00';
      warrantyDepositTotal = unitPriceWithTax * (warrantyDepositPercentage / 100);
    } else if (warrantyDepositType === TaxType.invoiceWithoutTax) {
      if (!unitPriceWithoutTax || isWarrantyDepositPercentageEmpty)
        return '0.00';
      warrantyDepositTotal = unitPriceWithoutTax * (warrantyDepositPercentage / 100);
    } else if (warrantyDepositType === TaxType.amountToFinance) {
      if (!financialAmount || isWarrantyDepositPercentageEmpty)
        return '0.00';
      warrantyDepositTotal = financialAmount * (warrantyDepositPercentage / 100);
    } else if (warrantyDepositType === TaxType.fixedAmount) {
      if (warrantyDepositFixedAmount == null || warrantyDepositFixedAmount == undefined)
        return '0.00';
      warrantyDepositTotal = parseFloat(warrantyDepositFixedAmount.toString());
    }

    return String(this.round2(warrantyDepositTotal));
  }

  convertCurrency(form: any) {
    const financialProduct = form.controls['financialProductType'].value;
    const currency = form.controls['currencyType'].value;// Moneda
    const quoteUsd = form.controls['quoteUsd'].value ?? 1;// Valor del Dolar (Puede ser 0)
    const tax = form.controls['tax'].value;
    const taxDecimal = Number(parseFloat(tax) / 100);
    const isConvertingToMXN = currency === 'MXN';
    // Guardar los resultados de la conversion
    const results: Record<string, any> = {};
    results['quoteUsd'] = quoteUsd;

    // 1. Convertir el Precio sin IVA
    const originalUnitPriceWithoutTax = parseFloat(form.controls['unitPriceWithoutTax'].value) || 0;
    const convertedUnitPriceWithoutTax = this.convertCurrencyValue(
      originalUnitPriceWithoutTax,
      quoteUsd,
      isConvertingToMXN
    );
    results['unitPriceWithoutTax'] = this.round2(convertedUnitPriceWithoutTax);

    // 2. Re-calcular el Precio con IVA
    const convertedUnitPriceWithTax = convertedUnitPriceWithoutTax * (1 + taxDecimal);
    results['unitPriceWithTax'] = this.round2(convertedUnitPriceWithTax);

    // 3. Enganche
    const downPaymentTaxType = form.controls['downPaymentTaxType'].value;
    if (downPaymentTaxType && downPaymentTaxType !== TaxType.fixedAmount) {
      // Re-calcular el enganche usando la función pure
      const downPaymentTax = form.controls['downPaymentTax'].value;
      results['downPayment'] = this.calculateDownPaymentAmount(
        downPaymentTaxType,
        downPaymentTax,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        0
      );
    } else if (downPaymentTaxType === TaxType.fixedAmount) {
      // Para monto fijo, aplicar conversión directa
      const currentDownPayment = parseFloat(form.controls['downPayment'].value) || 0;
      results['downPayment'] = this.round2(this.convertCurrencyValue(
        currentDownPayment,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    // 4. Convertir el monto a financiar
    const downPaymentAmount = parseFloat(results['downPayment'] || 0);
    const convertedFinancialAmount = this.calculateFinancialAmountValue(
      financialProduct,
      downPaymentAmount,
      convertedUnitPriceWithoutTax,
      convertedUnitPriceWithTax,
      tax
    );
    results['financialAmount'] = convertedFinancialAmount;

    // 5. La comisión por apertura se re-calcula
    const commissionForOpeningTaxType = form.controls['openingFeeType']?.value;
    if (commissionForOpeningTaxType) {
      const commissionForOpeningTax = form.controls['openingFeePercentage']?.value;
      const commisionFixedAmount = form.controls['openingFee']?.value;

      const calculatedCommissionWithoutTax = this.calculateCommissionForOpeningAmount(
        TaxType.invoiceWithoutTax,
        commissionForOpeningTax,
        commisionFixedAmount,
        downPaymentAmount,
        financialProduct,
        Number(convertedFinancialAmount),
        convertedUnitPriceWithTax,
        tax,
        convertedUnitPriceWithoutTax
      );
      const calculatedCommissionWithTax = this.calculateCommissionForOpeningAmount(
        TaxType.invoiceWithTax,
        commissionForOpeningTax,
        commisionFixedAmount,
        downPaymentAmount,
        financialProduct,
        Number(convertedFinancialAmount),
        convertedUnitPriceWithTax,
        tax,
        convertedUnitPriceWithoutTax
      );

      results['openingFeeWithoutTax'] = calculatedCommissionWithoutTax;
      results['openingFeeWithTax'] = calculatedCommissionWithTax;
    }

    // Subsidio
    const subsidyType = form.controls['subsidyType']?.value;
    if (subsidyType && subsidyType !== TaxType.fixedAmount) {
      const subsidyPercentage = form.controls['subsidyPercentage']?.value;
      const subsidyFixedAmount = form.controls['subsidyTotal']?.value;
      const { withTax, withoutTax } = this.getSubsidyFields(subsidyType);
      const subidyCalculatedRounded = this.calculateSubsidyAmount(
        subsidyType,
        subsidyPercentage,
        subsidyFixedAmount,
        tax,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        Number(convertedFinancialAmount)
      );
      const subsidyAdditional = this.calculateAdditional(subsidyType, subidyCalculatedRounded, tax);
      if (subsidyType == TaxType.invoiceWithTax) {
        results[withTax] = subidyCalculatedRounded;
        results[withoutTax] = subsidyAdditional;
      } else if (subsidyType == TaxType.invoiceWithoutTax || subsidyType == TaxType.amountToFinance) {
        results[withoutTax] = subidyCalculatedRounded;
        results[withTax] = subsidyAdditional;
      }
    } else if (subsidyType === TaxType.fixedAmount) {
      const currentSubsidyTotal = parseFloat(form.controls['subsidyTotal']?.value) || 0;
      results['subsidyTotal'] = this.round2(this.convertCurrencyValue(
        currentSubsidyTotal,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    // Opción de Compra
    const purchaseOptionType = form.controls['purchaseOptionType']?.value;
    if (purchaseOptionType && purchaseOptionType !== TaxType.fixedAmount) {
      const purchasePercentage = form.controls['purchasePercentage']?.value;
      const purchaseFixedAmount = form.controls['purchaseTotal']?.value;
      const { withTax, withoutTax } = this.getPurchaseFields(purchaseOptionType);
      const purchaseCalculatedRounded = this.calculatePurchasePure(
        purchaseOptionType,
        purchasePercentage,
        purchaseFixedAmount,
        tax,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        Number(convertedFinancialAmount)
      );
      const purchaseAdditional = this.calculateAdditional(purchaseOptionType, purchaseCalculatedRounded, tax);
      if (purchaseOptionType == TaxType.invoiceWithTax) {
        results[withTax] = purchaseCalculatedRounded;
        results[withoutTax] = purchaseAdditional;
      } else if (purchaseOptionType == TaxType.invoiceWithoutTax || purchaseOptionType == TaxType.amountToFinance) {
        results[withoutTax] = purchaseCalculatedRounded;
        results[withTax] = purchaseAdditional;
      }
    } else if (purchaseOptionType === TaxType.fixedAmount) {
      const currentPurchaseTotal = parseFloat(form.controls['purchaseTotal']?.value) || 0;
      results['purchaseTotal'] = this.round2(this.convertCurrencyValue(
        currentPurchaseTotal,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    // Valor Residual
    const residualValueType = form.controls['residualValueType']?.value;
    if (residualValueType && residualValueType !== TaxType.fixedAmount) {
      const residualValuePercentage = form.controls['residualValuePercentage']?.value;
      const residualValueFixedAmount = form.controls['residualValueTotal']?.value;
      const { withTax, withoutTax } = this.getResidualFields(residualValueType);
      const roundedResidualCalculated = this.calculateResidualValuePure(
        residualValueType,
        residualValuePercentage,
        residualValueFixedAmount,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        Number(convertedFinancialAmount)
      );
      const residualAdditional = this.calculateAdditional(residualValueType, roundedResidualCalculated, tax);
      if (residualValueType == TaxType.invoiceWithTax) {
        results[withTax] = roundedResidualCalculated;
        results[withoutTax] = residualAdditional;
      } else if (residualValueType == TaxType.invoiceWithoutTax || residualValueType == TaxType.amountToFinance) {
        results[withoutTax] = roundedResidualCalculated;
        results[withTax] = residualAdditional;
      }
    } else if (residualValueType === TaxType.fixedAmount) {
      const currentResidualValueTotal = parseFloat(form.controls['residualValueTotal']?.value) || 0;
      results['residualValueTotal'] = this.round2(this.convertCurrencyValue(
        currentResidualValueTotal,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    // Balloon
    const balloonType = form.controls['balloonType']?.value;
    if (balloonType && balloonType !== TaxType.fixedAmount) {
      const balloonPercentage = form.controls['balloonPercentage']?.value;
      const balloonFixedAmount = form.controls['balloon']?.value;
      results['balloon'] = this.calculateBalloonPure(
        balloonType,
        balloonPercentage,
        balloonFixedAmount,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        Number(convertedFinancialAmount)
      );
    } else if (balloonType === TaxType.fixedAmount) {
      const currentBalloon = parseFloat(form.controls['balloon']?.value) || 0;
      results['balloon'] = this.round2(this.convertCurrencyValue(
        currentBalloon,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    // Depósito en garantía
    const warrantyDeposit = form.controls['warrantyDeposit']?.value;
    if (warrantyDeposit && warrantyDeposit !== TaxType.fixedAmount) {
      const warrantyDepositPercentage = form.controls['warrantyDepositPercentage']?.value;
      const warrantyDepositFixedAmount = form.controls['warrantyDepositTotal']?.value;
      results['warrantyDepositTotal'] = this.calculateWarrantyDepositPure(
        warrantyDeposit,
        warrantyDepositPercentage,
        warrantyDepositFixedAmount,
        convertedUnitPriceWithTax,
        convertedUnitPriceWithoutTax,
        Number(convertedFinancialAmount)
      );
    } else if (warrantyDeposit === TaxType.fixedAmount) {
      const currentWarrantyDepositTotal = parseFloat(form.controls['warrantyDepositTotal']?.value) || 0;
      results['warrantyDepositTotal'] = this.round2(this.convertCurrencyValue(
        currentWarrantyDepositTotal,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    const confirmationExpensesAmount = form.controls['confirmationExpensesAmount']?.value;
    if (!!confirmationExpensesAmount) {
      results['confirmationExpensesAmount'] = this.round2(this.convertCurrencyValue(
        confirmationExpensesAmount,
        quoteUsd,
        isConvertingToMXN
      ));
    }

    return results;
  }

  /**
   * Función pura para convertir valores entre monedas
   * @param value - Valor a convertir
   * @param quoteUsd - Tipo de cambio USD
   * @param isUSDToMXN - true para convertir de USD a MXN, false para MXN a USD
   * @returns Valor convertido
   */
  private convertCurrencyValue(value: any, quoteUsd: number, isUSDToMXN: boolean): number {
    const numValue = parseFloat(value) || 0;
    if (numValue === 0 || quoteUsd === 0) return 0;
    return isUSDToMXN ? numValue * quoteUsd : numValue / quoteUsd;
  }


  getBodyforSaveDraft(form: FormGroup, formProcotizaResult?: any) {
    const incluedeInsurance = form.controls['includesInsurance']?.value;
    const insurance = form.controls['insurance']?.value;
    const model = {
      id: form.controls['id']?.value,
      creationDate: form.controls['creationDate']?.value,
      dealName: form.controls['dealName']?.value,
      isSelected: form.controls['isSelected']?.value,
      roRac: form.controls['roRac']?.value,
      currencyType: this.currencies.find((i) => i.value == form.controls['currencyType']?.value)?.type,
      quoteUsd: form.controls['quoteUsd']?.value,
      tax: form.controls['tax']?.value,
      taxFree: form.controls['taxFree']?.value,
      customerRate: form.controls['customerRate']?.value,
      financialProductType: form.controls['financialProductType']?.value,
      term: form.controls['term']?.value,
      productType: form.controls['productType']?.value,
      quantity: form.controls['quantity']?.value,
      feature: form.controls['feature']?.value,
      modelId: form.controls['modelId']?.value ? form.controls['modelId']?.value : null,
      openQuoteBrand: form.controls['openQuoteBrand']?.value,
      openQuoteSubBrand: form.controls['openQuoteSubBrand']?.value,
      openQuoteModel: form.controls['openQuoteModel']?.value,
      brandId: form.controls['brandId']?.value,
      subBrandId: form.controls['subBrandId']?.value,
      subBrandUrl: form.controls['subBrandUrl']?.value,
      opexSegment: form.controls['opexSegment']?.value,
      classification: form.controls['classification']?.value,
      useTime: form.controls['useTime']?.value,
      unitPriceWithoutTax: form.controls['unitPriceWithoutTax']?.value,
      unitPriceWithTax: form.controls['unitPriceWithTax']?.value,
      totalPriceWithoutTax: form.controls['totalPriceWithoutTax']?.value,
      totalPriceWithTax: form.controls['totalPriceWithTax']?.value,
      downPaymentTaxType: form.controls['downPaymentTaxType']?.value,
      downPaymentTax: form.controls['downPaymentTax']?.value,
      downPayment: form.controls['downPayment']?.value,
      financialAmount: formProcotizaResult?.financialAmount,
      openingFeeType: form.controls['openingFeeType']?.value,
      openingFee: form.controls['openingFee']?.value,
      openingFeePercentage: form.controls['openingFeePercentage']?.value,
      balloonType: form.controls['balloonType']?.value,
      balloon: form.controls['balloon']?.value,
      balloonPercentage: form.controls['balloonPercentage']?.value,
      warrantyDeposit: form.controls['warrantyDeposit']?.value,
      warrantyDepositMonth: form.controls['warrantyDepositMonth']?.value,
      warrantyDepositTotal: form.controls['warrantyDepositTotal']?.value,
      warrantyDepositPercentage: form.controls['warrantyDepositPercentage']?.value,
      subsidyType: form.controls['subsidyType']?.value,
      subsidyPercentage: form.controls['subsidyPercentage']?.value,
      subsidyTotal: form.controls['subsidyTotal']?.value,
      purchaseOptionType: form.controls['purchaseOptionType']?.value,
      purchasePercentage: form.controls['purchasePercentage']?.value,
      purchaseTotal: formProcotizaResult?.purchaseOptionAmount,
      residualValueType: form.controls['residualValueType']?.value,
      residualValuePercentage: form.controls['residualValuePercentage']?.value,
      residualValueTotal: (() => {
        const raw = form.controls['residualValueTotal']?.value;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      paymentType: form.controls['paymentType']?.value,
      paymentFormationType: form.controls['paymentFormationType'].value,
      confirmationExpensesAmount: form.controls['confirmationExpensesAmount']?.value,
      lifeInsuranceFactor: form.controls['lifeInsuranceFactor']?.value,
      additionalCollateralType: form.controls['additionalCollateralType']?.value ? form.controls['additionalCollateralType'].value : null,
      additionalCollateralPercentage: form.controls['additionalCollateralPercentage'].value,
      additionalCollateralTotal: form.controls['additionalCollateralTotal']?.value,
      profitDeal: form.controls['profitDeal']?.value && form.controls['profitDeal']?.value.length === 0 ? null : form.controls['profitDeal']?.value,
      specialPayments: form.controls['specialPayments']?.value,
      grace: form.controls['grace']?.value,
      irregularPayments: form.controls['irregularPayments'] ? form.controls['irregularPayments']?.value : [],
      payments: this.getPayments(form.controls['payments']?.value),
      result: form.controls['result']?.value,
      insurance: (!!insurance && incluedeInsurance == true) ? insurance : null,
      insuranceDeals: form.controls['insuranceDeals'] ? form.controls['insuranceDeals']?.value : [],
      includesInsurance: incluedeInsurance,
      year: form.controls['year']?.value,
    };
    return model;
  }

  getProductType(productType: productTypeEnum) {
    if (productType == productTypeEnum.accesorios) {
      return 'Accesorios';
    } else if (productType == productTypeEnum.autobuses) {
      return 'Autobuses';
    } else if (productType == productTypeEnum.camiones) {
      return 'Camiones';
    } else if (productType == productTypeEnum.tractos) {
      return 'Tractos';
    } else if (productType == productTypeEnum.nonDamiler) {
      return 'Non Daimler';
    } else if (productType == productTypeEnum.vanes) {
      return 'Vans';
    } else {
      return 'No aplica';
    }
  }

  getFinancialProduct(financialProductType: FinancialProductEnum) {
    if (financialProductType == FinancialProductEnum.directCredit) {
      return 'Crédito directo';
    } else if (financialProductType == FinancialProductEnum.financialLeasing) {
      return 'Arrendamiento financiero';
    } else if (financialProductType == FinancialProductEnum.pureLease) {
      return 'Arrendamiento puro';
    } else if (financialProductType == FinancialProductEnum.directCreditGlobe) {
      return 'Credito directo';
    } else if (financialProductType == FinancialProductEnum.capLoan) {
      return 'Capital Loan';
    } else if (financialProductType == FinancialProductEnum.realEstate) {
      return 'Real Estate';
    } else {
      return '';
    }
  }

  getSegmentOpex(segmentOpexType: SegmentOpexType): string {
    if (segmentOpexType == SegmentOpexType.freightLinerRetailBusiness) {
      return 'Retail Business';
    } else if (segmentOpexType == SegmentOpexType.freightLinerSmallFleets) {
      return 'Small Fleets';
    } else if (segmentOpexType == SegmentOpexType.freightLinerMediumFleets) {
      return 'Medium fleets';
    } else if (segmentOpexType == SegmentOpexType.freightLinerLargeFleets) {
      return 'Large fleets';
    } else if (segmentOpexType == SegmentOpexType.freightLinerExtraFleets) {
      return 'Extra large fleets';
    }

    return '';
  }

  getTaxType(taxType: TaxType): string {
    if (taxType == TaxType.invoiceWithTax) {
      return 'Valor con IVA';
    } else if (taxType == TaxType.invoiceWithoutTax) {
      return 'Valor sin IVA';
    } else if (taxType == TaxType.amountToFinance) {
      return 'Monto a financiar';
    } else if (taxType == TaxType.fixedAmount) {
      return 'Monto fijo';
    } else if (taxType == TaxType.numberOfRents) {
      return 'Número de rentas';
    } else if (taxType == 'UNIT_VALUE') {
      return 'Valor unidad';
    } else if (taxType == 'TOTAL_VALUE') {
      return 'Valor total';
    }

    return '';
  }

  getTypeClient(client: string): string {
    if (client == 'CV_FLEET') {
      return 'CV Fleets';
    } else if (client == 'CV_SMALL_BUSINESS') {
      return 'CV Small Business';
    } else if (client == 'DEALER') {
      return 'Dealer';
    }
    return '';
  }

  getPersonType(person: string): string {
    if (person == 'NATURAL_PERSON') {
      return 'Persona física';
    } else if (person == 'LEGAL_PERSON') {
      return 'Persona moral';
    }
    return '';
  }

  getClassificationType(classification: string): string {
    if (classification == 'NEW') {
      return 'Nuevo';
    } else if (classification == 'USED') {
      return 'Usado';
    }
    return 'No aplica';
  }

  _updatePayment(payment: any, listPayment: any) {
    if (listPayment == undefined) listPayment = [];
    let paymentSelected = {
      ...payment,
    };
    if (payment.paymentType == 'SPECIAL_PAYMENT') {
      paymentSelected.specialPaymentDate = payment.paymentDate;
    } else if (payment.paymentType == 'GRACE_PERIOD') {
      paymentSelected.gracePeriodDate = payment.paymentDate;
    } else if (payment.paymentType == 'IRREGULAR_PAYMENT') {
      paymentSelected.irregularPaymentDate = payment.paymentDate;
    }

    if (paymentSelected.id) {
      const index = listPayment.findIndex(
        (i: any) => i.id == paymentSelected.id
      );
      if (index !== -1) {
        listPayment[index] = paymentSelected;

        listPayment = listPayment.filter(
          (item: any, idx: number) =>
            item.period !== paymentSelected.period || idx === index
        );
      }
    } else {
      listPayment = listPayment.filter(
        (i: any) => i.period !== paymentSelected.period
      );
      listPayment.push(paymentSelected);
    }

    return listPayment;
  }

  getPayments(payments: any) {
    let nuevoArray = payments.map((i: any) => {
      return { ...i, paymentType: null };
    });
    return nuevoArray;
  }

  round2(value: number, decimals = 2): number {
    // Form controls often hold strings; coerce before + to avoid "1234.56" + EPSILON → NaN.
    // ponytail: factor stays decimals**10 (legacy); upgrade to 10**decimals when amounts can be re-baselined.
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return 0;
    }
    const factor = decimals ** 10;
    return parseFloat(
      (Math.round((num + Number.EPSILON) * factor) / factor).toFixed(decimals)
    );
  }

  /** Strip values Jackson cannot deserialize as BigDecimal (NaN / "NaN"). */
  isInvalidNumericPayload(value: unknown): boolean {
    return (
      value === 'NaN' ||
      (typeof value === 'number' && Number.isNaN(value))
    );
  }
}

